/**
 * treeScreenPartnerSpacing.ts — voetafdruk-bewuste partner-spacing voor het
 * PEDIGREE SCHERM-pad (opt-in via flag `tree.partnerSpacing`).
 *
 * Chirurgische port (approach A) van de bewezen export-logica
 * `applyVariableSizeAwareSpacing` (useTreeLayoutV2.ts:569-729), maar met
 * SCHERM-breedtes (geen PDF-rotatie). Lost NC #586 / #562 / #1039 op:
 * extra partners worden gender-neutraal aan de buitenkant geplaatst (even-Sosa
 * = vader → links, oneven = moeder → rechts) en de pair-pitch wordt verbreed
 * met hun voetafdruk zodat ze niet over buur-Sosa-kaarten vallen.
 *
 * Spec: docs/superpowers/specs/2026-06-02-tree-view-layout-requirements.md
 *   §3 (voetafdruk-definities), §4.1/§4.5 (regels), §4.10 (determinisme).
 *
 * IMMUTABLE-aanname: x = CENTER van de node (consistent met centerOnProband).
 * Zelfstandig: importeert alleen types. Constanten gespiegeld van
 * useTreeLayout.ts:38-52 (spec §7) — bewaakt door een drift-guard-test.
 */
import { plaatsGezinslijnen } from './treeGezinslijnen.ts';
import type { LayoutNode, LayoutLink } from '../types.ts';

// ─── Scherm-constanten (spec §7 — gespiegeld van useTreeLayout.ts:38-52) ──────
export const SCREEN_SPACING_CONSTANTS = {
  NODE_WIDTH: 230,
  COMPACT_NODE_WIDTH: 111,
  COMPACT_GEN: 4,
  H_GAP: 50,
  COMPACT_H_GAP: 10,
  SPOUSE_GAP: 10,
  COMPACT_SPOUSE_GAP: 8,
} as const;

const C = SCREEN_SPACING_CONSTANTS;

function screenCardWidth(gen: number): number {
  return gen >= C.COMPACT_GEN ? C.COMPACT_NODE_WIDTH : C.NODE_WIDTH;
}
function screenSpouseGap(gen: number): number {
  return gen >= C.COMPACT_GEN ? C.COMPACT_SPOUSE_GAP : C.SPOUSE_GAP;
}
function screenSiblingGap(gen: number): number {
  return gen >= C.COMPACT_GEN ? C.COMPACT_H_GAP : C.H_GAP;
}

/**
 * Map: anker-Sosa-node-id → lijst van zijn EXTRA (niet-voorouderlijke) spouse-
 * nodes. Een extra spouse is een non-Sosa LayoutNode die via een `spousal`-link
 * aan een Sosa-node hangt. Bloedlijn-paren (beide Sosa) tellen NIET mee.
 * (Equivalent van buildExtraSpouseMap in useTreeLayoutV2.ts:510-539.)
 *
 * NOTE: bewust gedupliceerd uit useTreeLayoutV2.ts — bij wijziging daar, hier mee aanpassen.
 */
function buildExtraSpouseMap(layout: {
  nodes: LayoutNode[];
  links: LayoutLink[];
}): Map<string, LayoutNode[]> {
  const nodeById = new Map<string, LayoutNode>();
  for (const n of layout.nodes) nodeById.set(n.person.id, n);
  const isSosa = (n: LayoutNode) => typeof n.sosaNumber === 'number' && n.sosaNumber >= 1;

  const result = new Map<string, LayoutNode[]>();
  for (const link of layout.links) {
    if (link.type !== 'spousal') continue;
    const a = nodeById.get(link.sourceId);
    const b = nodeById.get(link.targetId);
    if (!a || !b) continue;
    let sosaNode: LayoutNode | undefined;
    let spouse: LayoutNode | undefined;
    if (isSosa(a) && !isSosa(b)) {
      sosaNode = a;
      spouse = b;
    } else if (isSosa(b) && !isSosa(a)) {
      sosaNode = b;
      spouse = a;
    }
    if (!sosaNode || !spouse) continue;
    const arr = result.get(sosaNode.person.id) ?? [];
    if (!arr.some((s) => s.person.id === spouse!.person.id)) arr.push(spouse);
    result.set(sosaNode.person.id, arr);
  }
  return result;
}

/** Aantal extra spouse-nodes voor een gegeven Sosa-nummer (0 indien geen). */
// NOTE: bewust gedupliceerd uit useTreeLayoutV2.ts — bij wijziging daar, hier mee aanpassen.
function extraSpouseCount(
  nodeBySosa: Map<number, LayoutNode>,
  extraSpousesBySosaId: Map<string, LayoutNode[]>,
  sosaNum: number
): number {
  const node = nodeBySosa.get(sosaNum);
  if (!node) return 0;
  return extraSpousesBySosaId.get(node.person.id)?.length ?? 0;
}

/**
 * NC #842 (#1793) — pak het Sosa-rooster per paar. Gedeeld door het scherm
 * (`applyScreenPartnerSpacing`) en de export (`applyVariableSizeAwareSpacing`),
 * zodat beide hetzelfde principe volgen; elk geeft zijn eigen breedtes.
 *
 * Elk slot van generatie 1..maxGen telt mee, ook als er niemand staat (zoals het
 * oude uniforme rooster), dus zonder extra partners komt hier exact dat rooster
 * uit. Van boven naar beneden: elk kind krijgt de contour (meest linkse en meest
 * rechtse rand per generatie) van zijn voorouders; vader- en moederkant worden
 * zo dicht tegen elkaar gezet als die contouren per generatie toelaten. Het kind
 * staat midden tussen zijn ouders (Sosa-skelet, ouders gecentreerd erboven).
 *
 * @param voetafdruk [links, rechts] van het kaartmidden, incl. extra partners.
 * @param tussenruimte minimale ruimte tussen kaartranden op `gen`; `paar` = de
 *   vader en moeder van hetzelfde kind.
 * @returns sosa → x, proband op 0.
 */
export function pakSosaRooster(o: {
  maxGen: number;
  voetafdruk: (sosa: number, gen: number) => [number, number];
  tussenruimte: (gen: number, paar: boolean) => number;
}): Map<number, number> {
  // Contour per sosa: L[h]/R[h] = buitenrand op generatie gen+h, t.o.v. het eigen midden.
  const contour = new Map<number, { L: number[]; R: number[] }>();
  const afstand = new Map<number, number>(); // sosa → afstand vader↔moeder
  for (let gen = o.maxGen; gen >= 0; gen--) {
    for (let sosa = 2 ** gen; sosa < 2 ** (gen + 1); sosa++) {
      const [links, rechts] = o.voetafdruk(sosa, gen);
      const L = [-links];
      const R = [rechts];
      if (gen < o.maxGen) {
        const v = contour.get(2 * sosa)!;
        const m = contour.get(2 * sosa + 1)!;
        let d = 0;
        for (let h = 0; h < v.L.length; h++)
          d = Math.max(d, v.R[h] - m.L[h] + o.tussenruimte(gen + 1 + h, h === 0));
        afstand.set(sosa, d);
        for (let h = 0; h < v.L.length; h++) {
          L.push(Math.min(v.L[h] - d / 2, m.L[h] + d / 2));
          R.push(Math.max(v.R[h] - d / 2, m.R[h] + d / 2));
        }
        contour.delete(2 * sosa);
        contour.delete(2 * sosa + 1);
      }
      contour.set(sosa, { L, R });
    }
  }
  const x = new Map<number, number>([[1, 0]]);
  for (let sosa = 1; sosa < 2 ** o.maxGen; sosa++) {
    const d = afstand.get(sosa)!;
    x.set(2 * sosa, x.get(sosa)! - d / 2);
    x.set(2 * sosa + 1, x.get(sosa)! + d / 2);
  }
  return x;
}

/**
 * Herpositioneer een bestaande V1 PEDIGREE-layout in-place zodat extra partners
 * de buur-Sosa-kaarten niet meer overlappen. No-op als er geen extra partners
 * zijn (V1-layout blijft dan exact ongewijzigd → geen regressie).
 */
export function applyScreenPartnerSpacing(layout: {
  nodes: LayoutNode[];
  links: LayoutLink[];
}): void {
  if (layout.nodes.length === 0) return;

  // #1964 — een archiefvoorstel staat in het rooster op zijn Sosa-plek, zoals in `layoutPedigree`: de
  // boom maakt er een generatie voor (Twan 03-11), dus ook hier (eerst bewust buiten het rooster gelaten).
  const sosaNodes = layout.nodes.filter(
    (n) => typeof n.sosaNumber === 'number' && (n.sosaNumber as number) >= 1
  );
  if (sosaNodes.length === 0) return;

  // NC #601 — Sosa-nummers komen van de PROJECT-proband (rootId), niet van de
  // getoonde focus-persoon (useTreeLayout.ts "Assign Sosa numbers"). Is de focus
  // een VOOROUDER van de proband mét hourglass-nakomelingen in beeld, dan dragen
  // nodes op de nakomeling-rijen (generation < 0) dus óók Sosa-nummers
  // (proband=1, diens ouders=2/3, …). De rooster-herbouw hieronder neemt aan dat
  // "Sosa-node = voorouder-skelet van de focus" en zou die nakomelingen dwars
  // door hun (non-Sosa) broers/zussen schuiven (Emma's Taco-over-Fenna-bug).
  // Daarom hier een volledige bail-out. Bewuste trade-off:
  // (a) voor de NAKOMELING-rijen is V1 bewezen overlap-vrij mét aangrenzende
  //     koppels (unit-test useTreeLayout.hourglassOverlap + replica-meting op
  //     Emma's Gillis-project);
  // (b) de extra-partner-spacing vervalt dan óók voor de VOOROUDER-rijen — de
  //     #586-klasse (extra partner over buur-Sosa-kaart) kan daar in dichte
  //     configuraties terugkeren bij focus≠proband + hourglass;
  // (c) toch is niets doen hier het minst foute gedrag: bij focus≠proband is de
  //     rooster-herbouw sowieso ongeldig — Sosa is proband-relatief terwijl
  //     generation focus-relatief is (de focus krijgt bv. Sosa 6 op
  //     generation 0), dus de xBySosa-slotmapping mismatcht per definitie.
  if (sosaNodes.some((n) => n.generation < 0)) return;

  const extraSpousesBySosaId = buildExtraSpouseMap(layout);
  // Geen extra partners → niets te verbreden; laat V1 ongemoeid.
  if (extraSpousesBySosaId.size === 0) return;

  const nodeBySosa = new Map<number, LayoutNode>();
  for (const n of sosaNodes) nodeBySosa.set(n.sosaNumber as number, n);

  let maxGen = 0;
  for (const n of sosaNodes) if (n.generation > maxGen) maxGen = n.generation;
  if (maxGen === 0) return;

  // NC #842 (#1793) — afstand per paar. Tot 16 okt kreeg de hele toprij één
  // pitch, verbreed met de grootste extra-partnerbehoefte ergens in de boom
  // (B-09): één voorouder met twee extra partners maakte élk paar 478 i.p.v.
  // 240 px breed. Nu wordt het rooster per generatie op botsingen gepakt en
  // schuift alleen de omgeving van zo'n paar op. Bewuste afwijking van spec
  // §4.2 (gelijke breedte vader- en moederkant) — Twan, 16 okt.
  const toprijGap = screenSiblingGap(maxGen);
  const xBySosa = pakSosaRooster({
    maxGen,
    voetafdruk: (sosa, gen) => {
      const half = screenCardWidth(gen) / 2;
      const extra =
        extraSpouseCount(nodeBySosa, extraSpousesBySosaId, sosa) *
        (screenCardWidth(gen) + screenSpouseGap(gen));
      // even = vader → extra partners links; oneven = moeder → rechts.
      return sosa % 2 === 0 ? [half + extra, half] : [half, half + extra];
    },
    // Vader en moeder van hetzelfde kind: spouse-gap; verder overal de gap van
    // de toprij. Zo geeft een boom zonder extra partners exact het oude rooster
    // (pitch = max(paarbreedte, kaart rij eronder) + toprij-gap).
    tussenruimte: (gen, paar) => (paar ? screenSpouseGap(gen) : toprijGap),
  });

  // Re-center op proband (Sosa 1 → x=0).
  const probandNode = nodeBySosa.get(1);
  const offsetX = xBySosa.get(1) ?? 0;

  const oldXById = new Map<string, number>();
  for (const n of layout.nodes) oldXById.set(n.person.id, n.x);

  for (const n of sosaNodes) {
    const newX = xBySosa.get(n.sosaNumber as number);
    if (newX === undefined) continue;
    n.x = newX - offsetX;
  }

  // Plaats extra partners aan de buitenkant op Sosa-basis (gender-neutraal):
  // even-Sosa = vader → links; oneven = moeder → rechts.
  const placedSpouseIds = new Set<string>();
  for (const anchor of sosaNodes) {
    const spouses = extraSpousesBySosaId.get(anchor.person.id);
    if (!spouses || spouses.length === 0) continue;
    const sosaNum = anchor.sosaNumber as number;
    // #1793 — de focus (generatie 0) met twee of meer partners staat tussen hen in
    // (V1, partnerPlekken). Dat geldt ook als de focus een voorouder van de proband
    // is (Sosa ≥ 2): eerder sloeg dit alleen Sosa 1 over en zette de pass zijn
    // partners daarna nog eens aan de vaderkant, bovenop V1's linkerpartner
    // (reality-check prod-kopie, 16 okt). Zijn partners schuiven met hem mee.
    // Tel de hele focusrij: een partner met een eigen Sosa-nummer (de moeder in de
    // Sosa-lijn) staat niet in extraSpousesBySosaId, maar wel naast hem.
    const focusRij =
      anchor.generation === 0 ? layout.nodes.filter((n) => n !== anchor && n.generation === 0) : [];
    if (focusRij.length >= 2) {
      const delta = anchor.x - (oldXById.get(anchor.person.id) ?? anchor.x);
      for (const sp of focusRij) {
        sp.x = (oldXById.get(sp.person.id) ?? sp.x) + delta;
        sp.y = anchor.y;
        placedSpouseIds.add(sp.person.id);
      }
      continue;
    }
    const isFather = sosaNum % 2 === 0;
    const unit = screenCardWidth(anchor.generation) + screenSpouseGap(anchor.generation);
    spouses.forEach((sp, idx) => {
      const k = idx + 1;
      sp.x = anchor.x + (isFather ? -k * unit : k * unit);
      sp.y = anchor.y;
      placedSpouseIds.add(sp.person.id);
    });
  }

  // Overige non-Sosa nodes (bv. nakomelingen in hourglass-modus) volgen de
  // proband-delta. Defensief vangnet: het scherm-pad gate't op pure PEDIGREE,
  // die doorgaans géén descendant-nodes heeft — geen hot path, wel toekomstvast.
  if (probandNode) {
    const oldProbandX = oldXById.get(probandNode.person.id) ?? 0;
    const probandDelta = probandNode.x - oldProbandX;
    for (const n of layout.nodes) {
      if (typeof n.sosaNumber === 'number' && (n.sosaNumber as number) >= 1) continue;
      if (placedSpouseIds.has(n.person.id)) continue;
      n.x = (oldXById.get(n.person.id) ?? n.x) + probandDelta;
    }
  }

  // Re-sync link-coördinaten na de x-rewrite.
  const xById = new Map<string, number>();
  for (const n of layout.nodes) xById.set(n.person.id, n.x);
  for (const link of layout.links) {
    const sx = xById.get(link.sourceId);
    const tx = xById.get(link.targetId);
    if (sx !== undefined) link.sourceX = sx;
    if (tx !== undefined) link.targetX = tx;
    if (link.childXs !== undefined) {
      link.childXs = link.childXs.map((_, idx) => {
        const childId = link.childIds?.[idx];
        return childId ? (xById.get(childId) ?? link.childXs![idx]) : link.childXs![idx];
      });
    }
  }
  // #1590 — de re-sync hierboven zet gezinslijnen terug op de ouder; zet ze weer
  // vanuit het eigen paar.
  plaatsGezinslijnen(layout.nodes, layout.links);
}
